'use client';

import { useState } from 'react';
import styled from 'styled-components';

import { ACCENT, ACCENT_GRAD, ON_ACCENT, TEXT } from './constants';

const KLAVIYO_COMPANY_ID = 'Yrca4a';
const KLAVIYO_LIST_ID = 'XYfvGR';
const KLAVIYO_REVISION = '2026-07-15';

const subscribeToKlaviyo = (email: string) =>
  fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${KLAVIYO_COMPANY_ID}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      revision: KLAVIYO_REVISION,
    },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Metra landing page',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: KLAVIYO_LIST_ID,
            },
          },
        },
      },
    }),
  });

const JoinForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 36px;

  input {
    flex: 1 1 280px;
    min-height: 56px;
    padding: 0 22px;
    border-radius: 999px;
    border: 1px solid rgba(245, 238, 234, 0.16);
    background: rgba(8, 8, 7, 0.5);
    color: ${TEXT};
    font-size: 16.5px;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: rgba(245, 238, 234, 0.32);
    }

    &:focus {
      border-color: rgba(255, 146, 115, 0.6);
    }
  }

  button {
    flex: 1 1 100%;
    min-height: 56px;
    padding: 0 30px;
    border: none;
    border-radius: 999px;
    background: ${ACCENT_GRAD};
    color: ${ON_ACCENT};
    font-family: inherit;
    font-size: 16.5px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 18px 44px -20px rgba(255, 122, 84, 0.9);
    transition: transform 0.2s ease;

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      flex: 0 1 auto;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    &:hover {
      transform: translateY(-1px);
    }
  }
`;

const Confirmation = styled.div`
  margin-top: 36px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px;
  border-radius: 18px;
  border: 1px solid rgba(255, 146, 115, 0.3);
  background: rgba(255, 122, 84, 0.09);
  font-size: 16.5px;
  color: rgba(245, 238, 234, 0.9);

  .dot {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${ACCENT};
  }
`;

const ErrorNote = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  color: #ff9d8a;
`;

const EarlyAccessForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;

    setStatus('submitting');
    try {
      const res = await subscribeToKlaviyo(email);
      setStatus(res.ok ? 'submitted' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'submitted') {
    return (
      <Confirmation>
        <span className="dot" />
        <span>
          One quick thing — we sent a confirmation email to <strong>{email}</strong>.
          Click it and you&apos;re officially first in line.
        </span>
      </Confirmation>
    );
  }

  return (
    <>
      <JoinForm onSubmit={onSubmit}>
        <input
          type="email"
          required
          placeholder="you@work.email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Sending…' : 'Request invite'}
        </button>
      </JoinForm>
      {status === 'error' && (
        <ErrorNote>Something went wrong — mind trying again?</ErrorNote>
      )}
    </>
  );
};

export default EarlyAccessForm;
